import _extends from "@babel/runtime/helpers/esm/extends";
import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'emotion-theming';
import { themeGet } from '@styled-system/theme-get';
import { Select as MuiSelect, MenuItem as MuiMenuItem } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { compose, layout, border, space, typography, color } from 'styled-system';
import { IconChevronDown, IconCheckmark } from '@adapt-design-system/icons';
import { baseInputProps } from './TextInput';
import { getStyledFunctionsNames } from '../utils';
import { getColorMode } from '../ColorModeProvider';
import Box from '../Box';
var styledFunctions = {
  layout: layout,
  border: border,
  space: space,
  typography: typography,
  color: color
};
/* Outer span required for Mui, which applies Mui classNames to */

var _ref =
/*#__PURE__*/
React.createElement(IconChevronDown, null);

var Icon = function Icon(props) {
  return React.createElement("span", props, _ref);
};
/* 
    Variables used to determine if parent component should re-render. See 
    https://github.com/Bain/adapt-design-system/issues/216
 */


var StyledSelect, previousColorMode;

var Select = function Select(props) {
  var borderColorHover = props.borderColorHover,
      borderRadius = props.borderRadius,
      children = props.children,
      disabled = props.disabled,
      disabledBgColor = props.disabledBgColor,
      disabledOpacity = props.disabledOpacity,
      displayEmpty = props.displayEmpty,
      fullWidth = props.fullWidth,
      IconComponent = props.IconComponent,
      input = props.input,
      inputProps = props.inputProps,
      inputRef = props.inputRef,
      isNative = props.isNative,
      multiple = props.multiple,
      onChange = props.onChange,
      placeholderHoverColor = props.placeholderHoverColor,
      renderValue = props.renderValue,
      value = props.value;
  var menuClasses = makeStyles({
    list: {
      backgroundColor: themeGet('colors.background')(props)
    }
  }, {
    name: 'SelectList'
  })();
  var colorMode = getColorMode();
  /* 
    See #216. Here we explicitly check if the component needs to re-render.  
  */

  if (!StyledSelect || colorMode !== previousColorMode) {
    StyledSelect = withStyles({
      root: _extends({
        height: 'initial'
      }, compose(layout, border, space, typography, color)(props)),
      select: {
        '&:hover:not(disabled)': {
          borderColor: themeGet("colors.".concat(borderColorHover))(props)
        },
        '&::placeholder': {
          color: themeGet("colors.".concat(placeholderHoverColor))(props),
          opacity: 1
        },
        '&:focus': {
          backgroundColor: 'transparent',
          borderColor: themeGet("colors.focus", '#4688F1')(props),
          boxShadow: "".concat(themeGet('shadows.60', '#4688F1')(props), ",\n                    inset 0 0 0 1px ").concat(themeGet("colors.focus", '#4688F1')(props)),
          outline: 0,
          borderRadius: themeGet("radii.".concat(borderRadius))(props)
        }
      },
      icon: {
        color: themeGet("colors.subduedText")(props),
        opacity: 0.5,
        right: themeGet("space.".concat(props.p))(props),
        top: "calc(50% - 10px)"
      },
      disabled: {
        backgroundColor: themeGet("colors.".concat(disabledBgColor))(props),
        border: 0,
        opacity: "".concat(disabledOpacity, " !important")
      }
    }, {
      name: 'Select'
    })(MuiSelect);
    previousColorMode = colorMode;
  }

  return React.createElement(StyledSelect, {
    disabled: disabled,
    disabledOpacity: disabledOpacity,
    disableUnderline: true,
    displayEmpty: displayEmpty,
    fullWidth: fullWidth,
    IconComponent: IconComponent,
    input: input,
    inputProps: inputProps,
    inputRef: inputRef,
    MenuProps: {
      classes: menuClasses
    },
    multiple: multiple,
    "native": isNative,
    onChange: onChange,
    renderValue: renderValue,
    value: value
  }, children);
};

Select.defaultProps = _extends({}, baseInputProps, {
  IconComponent: Icon
});
Select.styledFunctions = getStyledFunctionsNames(styledFunctions);

var _ref2 =
/*#__PURE__*/
React.createElement(IconCheckmark, null);

var MenuItem = function MenuItem(props) {
  var children = props.children,
      value = props.value,
      selectedItemColor = props.selectedItemColor,
      selectedItemBg = props.selectedItemBg,
      onClick = props.onClick,
      selected = props.selected,
      autoFocus = props.autoFocus,
      disabled = props.disabled;
  var StyledSelectMenuItem = withStyles({
    root: _extends({}, compose(typography, color)(props))
  }, {
    name: 'SelectItem'
  })(MuiMenuItem);
  var getListItemStyles = makeStyles({
    root: {
      '&$focusVisible, &$focusVisible&$selected': {
        backgroundColor: themeGet("colors.".concat(selectedItemBg))(props),
        color: themeGet("colors.".concat(selectedItemColor))(props)
      },
      '&$selected, &$selected:hover': {
        backgroundColor: 'transparent',
        color: themeGet('colors.text')(props)
      },
      '&$selected, &$selected;hover, &$button:hover, &:hover': {
        backgroundColor: themeGet("colors.".concat(selectedItemBg))(props),
        color: themeGet("colors.".concat(selectedItemColor))(props)
      }
    },
    disabled: {
      color: themeGet("colors.subduedText")(props)
    },
    button: {
      '&:hover': {
        backgroundColor: themeGet("colors.".concat(selectedItemBg))(props),
        color: themeGet("colors.".concat(selectedItemColor))(props)
      }
    },

    /*
        This empty object is required for JSS to properly render the above class for specificity
        https://material-ui.com/customization/components/#use-rulename-to-reference-a-local-rule-within-the-same-style-sheet
    */
    focusVisible: {},
    selected: {
      '&:hover': {
        backgroundColor: themeGet("colors.".concat(selectedItemBg))(props),
        color: themeGet("colors.".concat(selectedItemColor))(props)
      }
    }
  });
  return React.createElement(StyledSelectMenuItem
  /* eslint-disable jsx-a11y/no-autofocus */
  , {
    autoFocus: autoFocus,
    disabled: disabled,
    disableRipple: true,
    ListItemClasses: getListItemStyles(),
    onClick: onClick,
    selected: selected,
    value: value
  }, React.createElement(Box, {
    width: 20,
    flexShrink: 0
  }, selected && _ref2), children);
};

MenuItem.defaultProps = {
  bg: 'background',
  color: 'text',
  fontFamily: 'default',
  fontSize: 'body',
  selectedItemBg: 'accent',
  selectedItemColor: 'white'
};
var SelectMenuItem = withTheme(MenuItem);
export default withTheme(Select);
export { SelectMenuItem };